import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { 
  FaEnvelope, 
  FaMicrosoft, 
  FaGoogle, 
  FaTimes, 
  FaYahoo, 
  FaGlobe, 
  FaDesktop, 
  FaChrome
} from 'react-icons/fa';
import css from './EmailClientModal.module.css';

interface EmailClientModalProps {
  onCancel: () => void;
  emailAddress: string;
}

const EmailClientModal: React.FC<EmailClientModalProps> = ({
  onCancel,
  emailAddress
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Prevent background scrolling when modal is open
    document.body.style.overflow = 'hidden';
    
    // Handle escape key to close
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    
    document.addEventListener('keydown', handleEscKey);
    
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onCancel]);

  const openEmailClient = (client: 'mail' | 'outlook' | 'gmail' | 'yahoo' | 'outlookWeb') => {
    switch (client) {
      case 'mail':
        window.location.href = `mailto:${emailAddress}`;
        break;
      case 'gmail':
        window.open(`https://mail.google.com/mail/?view=cm&to=${emailAddress}`, '_blank');
        break;
      case 'yahoo':
        window.open(`https://compose.mail.yahoo.com/?to=${emailAddress}`, '_blank');
        break;
      case 'outlookWeb':
        window.open(`https://outlook.office.com/mail/deeplink/compose?to=${emailAddress}`, '_blank');
        break;
    }
    onCancel();
  };

  // Create the modal content
  const modalContent = (
    <div className={css.modalOverlay} onClick={(e) => {
      // Close when clicking the overlay (outside the modal)
      if (e.target === e.currentTarget) {
        onCancel();
      }
    }}>
      <div className={css.modal} ref={modalRef}>
        <button 
          className={css.closeButton} 
          onClick={onCancel}
          aria-label="Close modal"
        >
          <FaTimes />
        </button>
        
        <div className={css.modalHeader}>
          <h2>How would you like to reach Atlas Support?</h2>
        </div>
        
        <div className={css.modalContent}>
          <p>
            Choose your preferred way to contact our support team:
          </p>
          <div className={css.clientOptions}>
            <button 
              className={css.clientButton}
              onClick={() => openEmailClient('mail')}
            >
              <FaEnvelope className={css.clientIcon} />
              <span>Mail App</span>
            </button>
            
            <button 
              className={css.clientButton}
              onClick={() => openEmailClient('gmail')}
            >
              <FaGoogle className={css.clientIcon} />
              <span>Gmail</span>
            </button>
            
            <button 
              className={css.clientButton}
              onClick={() => openEmailClient('yahoo')}
            >
              <FaYahoo className={css.clientIcon} />
              <span>Yahoo Mail</span>
            </button>
            
            <button 
              className={css.clientButton}
              onClick={() => openEmailClient('outlookWeb')}
            >
              <div className={css.compositeIconContainer}>
                <FaMicrosoft className={css.clientIcon} />
                <FaGlobe className={css.badgeIcon} />
              </div>
              <span>Outlook</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use React Portal to render the modal at the document body level
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default EmailClientModal;